import { BASE_URL } from "../utils/constants.ts";

interface UserDetails {
  id: string;
  name: string;
  email: string;
}

interface TipDetails {
  id: string;
  name: string;
  email: string;
}

interface ReferAndEarn {
  userId: string;
  token: string;
}

interface InviteLinkDetails {
  inviteeEmail: string;
  inviterId: string;
}
interface UserData {
  userId: string;
  amount: number;
}
export const fetchUserDetails = async (
  userId: string,
  token: string
): Promise<UserDetails> => {
  const response = await fetch(`${BASE_URL}/userDetails/${userId}`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: token,
    },
  });

  if (!response.ok) {
    throw new Error("Failed to fetch user details");
  }

  const userData = await response.json();
  return userData as UserDetails; // Assuming the response data matches the UserDetails interface
};

export const fetchTipDetails = async (
  userId: string,
  token: string
): Promise<TipDetails> => {
  try {
    const response = await fetch(`${BASE_URL}/tipDetails/${userId}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: token, // Include the token in the request headers
      },
    });

    if (!response.ok) {
      throw new Error("Failed to fetch tip details details");
    }

    const tipData = await response.json();
    return tipData;
  } catch (error) {
    console.error("Error in fetchTipDetails:", error);
    throw error; // Re-throw the error to be caught by the caller (fetchMyTipDetails)
  }
};

export const fetchReferAndEarn = async (
  userId: string,
  token: string,
  currentPage: number,
  itemsPerPage: number
) => {
  try {
    const response = await fetch(
      `https://app.tipanonymous.com/api/invite/ReferralStats/${userId}?page=${itemsPerPage}&limit=${currentPage}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
      }
    );

    if (!response.ok) {
      throw new Error("Failed to fetch tip details details");
    }

    const referAndEarn = await response.json();
    return referAndEarn;
  } catch (error) {
    console.error("Error in ReferAndEarn:", error);
    throw error;
  }
};

export const sendInviteLink = async (inviteLinkDetails: InviteLinkDetails) => {
  const response = await fetch(
    "https://app.tipanonymous.com/api/invite/sendInvitelink",
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(inviteLinkDetails),
    }
  );
  let data = await response.json();

  if (!response.ok) {
    return { response, data };
  }
  return data;
};

export const addBankAccount = async (bankDetails: any) => {
  const response = await fetch(BASE_URL + "/createStripeAccountForReciver", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(bankDetails),
  });
  let data = await response.json();

  if (!response.ok) {
    return { response, data };
  }
  return data;
};

export const fetchBankDeatils = async (userId: string, token: string) => {
  const response = await fetch(BASE_URL + `/addBankDetails/${userId}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });
  let data = await response.json();
  if (!response.ok) {
    return { response, data };
  }
  return data;
};

export const removeBankAccount = async (userId: string, token: string) => {
  const response = await fetch(BASE_URL + `/deleteBankDetails/${userId}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });
  let data = await response.json();
  if (!response.ok) {
    return { response, data };
  }
  return data;
};

export const getTranscationDetails = async (token: string, userId: string) => {
  const response = await fetch(
    BASE_URL + `/TranscationDetails?userId=${userId}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    }
  );
  let data = await response.json();
  if (!response.ok) {
    return { response, data };
  }
  return data;
};

export const collectMyTips = async (token: string, userData: UserData) => {
  const response = await fetch(BASE_URL + `/transferAmount`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(userData),
  });
  let data = await response.json();
  if (!response.ok) {
    return { response, data };
  }
  return data;
};

export const inviteByReferalCode = async (
  email: string,
  referredCode: string
) => {
  const response = await fetch(
    "https://app.tipanonymous.com/api/invite/InviteByCopy",
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        email: email,
        referredCode: referredCode,
      }),
    }
  );
  let data = await response.json();
  if (!response.ok) {
    return { response, data };
  }
  return data;
};

export const generateStripeLoginLink = async (
  connectedAcID: string,
  token: string
) => {
  const response = await fetch(BASE_URL + `/loginLink`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      accountId: connectedAcID,
    }),
  });
  let data = await response.json();
  if (!response.ok) {
    return { response, data };
  }
  return data;
};

export const getConnectedAccountStatus = async (
  connectedAcID: string,
  token: string
) => {
  const response = await fetch(BASE_URL + `/connectedAccountStatus`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      accountId: connectedAcID,
    }),
  });
  let data = await response.json();
  if (!response.ok) {
    return { response, data };
  }
  return data;
};

export const updatePersonalizedText = async (
  id: string,
  message: string,
  token: string
) => {
  const response = await fetch(BASE_URL + `/addPersonalizedText`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      id: id,
      message: message,
    }),
  });
  let data = await response.json();
  if (!response.ok) {
    return { response, data };
  }
  return data;
};
